import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect } from 'react';

export type UserRole = 'admin' | 'teacher' | 'student' | null;

const ACTIVE_ROLE_KEY = 'activeRole';

export const useRole = () => {
  const { user, isAuthenticated } = useAuth();
  const [activeRole, setActiveRole] = useState<UserRole>(() => {
    return (localStorage.getItem(ACTIVE_ROLE_KEY) as UserRole) || null;
  });
  
  const { data: roles, isLoading, refetch } = useQuery({
    queryKey: ['userRoles', user?.id],
    queryFn: async (): Promise<UserRole[]> => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Error fetching user roles:', error);
        return [];
      }
      
      return data?.map(r => r.role as UserRole) || [];
    },
    enabled: isAuthenticated && !!user?.id,
  });

  // Set active role when roles are loaded
  useEffect(() => {
    if (roles && roles.length > 0) {
      const savedRole = localStorage.getItem(ACTIVE_ROLE_KEY) as UserRole;
      if (savedRole && roles.includes(savedRole)) {
        setActiveRole(savedRole);
      } else {
        setActiveRole(roles[0]);
        localStorage.setItem(ACTIVE_ROLE_KEY, roles[0]);
      }
    }
  }, [roles]);

  const assignRole = async (roleToAssign: 'teacher' | 'student') => {
    if (!user?.id) return { error: 'No user found' };
    
    // Check if role already exists
    if (roles?.includes(roleToAssign)) {
      return { error: 'Role already assigned' };
    }
    
    const { error } = await supabase
      .from('user_roles')
      .insert({ user_id: user.id, role: roleToAssign });
    
    if (error) {
      console.error('Error assigning role:', error);
      return { error };
    }
    
    await refetch();
    return { error: null };
  };

  const switchRole = (newRole: UserRole) => {
    if (newRole && roles?.includes(newRole)) {
      setActiveRole(newRole);
      localStorage.setItem(ACTIVE_ROLE_KEY, newRole);
    }
  };

  return {
    role: activeRole,
    roles: roles || [],
    isLoading,
    isTeacher: activeRole === 'teacher',
    isStudent: activeRole === 'student',
    isAdmin: (roles || []).includes('admin'),
    hasMultipleRoles: (roles?.length || 0) > 1,
    assignRole,
    switchRole,
    refetch,
  };
};
